import {UserGroup, UserGroupPrivilege} from "../user-groups/UserGroup";
import {Identifier} from "../common/Identifier";
import {AuthenticatedUserState} from "../../reducers/common/AuthenticatedUserReducer";

/**
 * Represents a unique identifier for a user in the system.
 *
 * This interface is used to enforce the structure and type of user identifiers
 * across the application, ensuring consistency when referencing users. It extends
 * the `Identifier` interface and expects a numeric value to represent the user ID.
 *
 * @template T The type of the identifier value, constrained to `number`.
 */
export interface UserId extends Identifier<number> {}

/**
 * Converts a numeric identifier into a UserId object.
 *
 * This function standardizes the representation of user identifiers
 * by encapsulating the numeric ID within a structured UserId object.
 *
 * @param id - The numeric user identifier to be converted. Must be a valid integer.
 * @return A UserId object containing the provided numeric identifier as its rawValue property.
 * @throws Will throw an error if the input is not a valid number or falls outside expected constraints (e.g., non-integer values).
 */
export function toUserId(id: number ): UserId { return { rawValue: id }; }

/**
 * Represents an authenticated user within the system.
 *
 * This interface captures the essential attributes and permissions of a user
 * after successful authentication. It serves as a structured representation
 * of user-related data and access controls necessary for role-based operations
 * within the application.
 */
export interface AuthenticatedUser {
    id: UserId
    email: string
    firstName: string
    lastName: string
    shorthandName: string
    userGroups: UserGroup[] | null
    generalPrivileges: { [ key: string ]: UserGroupPrivilege }
    isEnabled: boolean
    isDeleted: boolean
    isCredentialsNonExpired: boolean
    isAccountNonExpired: boolean
    isAccountNonLocked: boolean
    companyId: number
}

/**
 * Provides a structure to inject authenticated user state into components or services.
 *
 * This interface is used to ensure the availability of user authentication details
 * throughout the application. It serves a critical role in enabling user-specific
 * functionality and maintaining secure access to protected resources.
 *
 * @property authUser - Represents the state of the currently authenticated user.
 * This includes user-specific information and metadata necessary for authentication.
 */
export interface AuthenticatedUserInjectedProp {
    authUser: AuthenticatedUserState
}

/**
 * Represents a property that enforces the presence of a user identifier.
 *
 * This interface ensures that any object implementing it includes a `userId` property, which is required
 * to uniquely identify a user across the system. It is primarily used in entities or requests involving
 * specific user-related data.
 */
export interface UserIdProp {
    userId: UserId
}

/**
 * Creates and returns a default guest user configuration.
 *
 * This method initializes a user object specifically for guest access.
 * It provides a pre-defined set of properties representing a system-controlled guest user.
 * Typically, this is used for temporary access or as a placeholder before full user authentication.
 *
 * Guest users are assigned a unique ID and are restricted from performing actions that require authentication.
 * By design, the guest user's credentials, account status, and privileges are deactivated.
 *
 * @return {AuthenticatedUser} The default guest user object with limited access and system-designated properties.
 */
export function initialGuestUser(): AuthenticatedUser {
    return {
        id: toUserId(  0),
        email: "guest@gibproject.de",
        firstName: "GUEST",
        lastName: "GUEST",
        shorthandName: "",
        userGroups: null,
        generalPrivileges: {  },
        isEnabled: false,
        isDeleted: false,
        isCredentialsNonExpired: false,
        isAccountNonExpired: false,
        isAccountNonLocked: false,
        companyId: 0
    };
}